import { useTranslation } from 'react-i18next';
import { Alert, Button } from '../ui';
import { useOnlineStatus } from '../../hooks/useOnlineStatus.js';
import { ViewState } from './ViewState.jsx';
import './states.css';

/**
 * The offline state of spec §11 in its two shapes:
 *
 *   - `variant="banner"` (default) — a non-blocking strip above content the
 *     user can still read. Nothing is hidden, because being offline does not
 *     invalidate what is already on screen (§11.1: never lose visible work).
 *   - `variant="panel"` — the full ViewState, for a surface that has nothing
 *     to show without the network.
 *
 * The component watches the connection itself, so callers can render it
 * unconditionally; it returns `null` while online unless `force` is set.
 *
 * @param {object} props
 * @param {'banner'|'panel'} [props.variant='banner']
 * @param {boolean} [props.force=false] - render even when the browser reports online.
 * @param {import('react').ReactNode} [props.description] - overrides the default explanation.
 * @param {() => void} [props.onReconnect] - called after the connection is confirmed back.
 * @param {boolean} [props.compact=false]
 * @param {string} [props.className]
 */
export function OfflineNotice({
  variant = 'banner',
  force = false,
  description,
  onReconnect,
  compact = false,
  className = '',
  ...rest
}) {
  const { t } = useTranslation();
  const { online, checking, reconnect } = useOnlineStatus({ onReconnect });

  if (online && !force) return null;

  const check = () => {
    reconnect();
  };

  if (variant === 'panel') {
    return (
      <ViewState
        status="offline"
        description={description}
        onRetry={check}
        busy={checking}
        compact={compact}
        className={className}
        {...rest}
      />
    );
  }

  return (
    <div className={`etch-state-banner ${className}`.trim()} {...rest}>
      <Alert tone="warning" title={t('states.offline')}>
        <div className="etch-state-banner__body">
          <span className="etch-state-banner__text">{description ?? t('states.offlineHint')}</span>
          <Button variant="secondary" size="compact" onClick={check} loading={checking}>
            {t('states.checkConnection')}
          </Button>
        </div>
      </Alert>
    </div>
  );
}

export default OfflineNotice;
